/**
 * Curation Page — curated feeds only.
 *
 * Shows articles with status = 'browsing' from curation-enabled feeds.
 * Editor can:
 * - Ad-hoc Import → select article for individual rewrite (standard tier)
 * - Batch Import → select article for batch rewrite (batch tier, cheaper)
 * - Dismiss → permanent archive (never resurfaces)
 */
import { useEffect, useState, useCallback } from "react";
import { api, type BrowseArticle, type Feed } from "../lib/api.ts";
import { relativeTime, truncate } from "../lib/utils.ts";

export default function Curate() {
  const [articles, setArticles]     = useState<BrowseArticle[]>([]);
  const [feeds, setFeeds]           = useState<Feed[]>([]);
  const [selectedFeed, setSelectedFeed] = useState<string>("");
  const [loading, setLoading]       = useState(true);
  const [actionMap, setActionMap]   = useState<Record<number, "adhoc" | "batch" | "dismissing" | "done">>({});
  const [batchCount, setBatchCount] = useState(0);

  const loadData = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (selectedFeed) params.feed_id = selectedFeed;
    Promise.all([
      api.articles.curate(params),
      api.feeds.list(),
    ])
      .then(([curateResult, feedsList]) => {
        setArticles(curateResult.articles);
        setFeeds(feedsList.filter(f => f.curation_enabled));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedFeed]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleAdhoc(id: number) {
    setActionMap(m => ({ ...m, [id]: "adhoc" }));
    try {
      await api.articles.select(id, "standard");
      setActionMap(m => ({ ...m, [id]: "done" }));
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      alert(`Error importing article: ${String(e)}`);
      setActionMap(m => { const n = { ...m }; delete n[id]; return n; });
    }
  }

  async function handleBatch(id: number) {
    setActionMap(m => ({ ...m, [id]: "batch" }));
    try {
      await api.articles.select(id, "batch");
      setActionMap(m => ({ ...m, [id]: "done" }));
      setArticles(prev => prev.filter(a => a.id !== id));
      setBatchCount(c => c + 1);
    } catch (e) {
      alert(`Error queuing article for batch: ${String(e)}`);
      setActionMap(m => { const n = { ...m }; delete n[id]; return n; });
    }
  }

  async function handleDismiss(id: number) {
    const note = prompt("Dismiss note (optional):");
    if (note === null) return;
    setActionMap(m => ({ ...m, [id]: "dismissing" }));
    try {
      await api.articles.dismiss(id, note);
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      alert(`Error dismissing: ${String(e)}`);
      setActionMap(m => { const n = { ...m }; delete n[id]; return n; });
    }
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Curation</h1>
          <p className="text-gray-500 text-sm mt-1">
            UK Government feeds — import articles for rewrite or dismiss to archive
          </p>
        </div>
        <div className="flex items-center gap-3">
          {batchCount > 0 && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {batchCount} queued for batch
            </span>
          )}
          <button onClick={loadData} className="btn-ghost btn btn-sm">
            Refresh
          </button>
        </div>
      </div>

      {/* Feed filter */}
      {feeds.length > 0 && (
        <div className="mb-5">
          <select
            value={selectedFeed}
            onChange={e => setSelectedFeed(e.target.value)}
            className="input max-w-xs text-sm"
          >
            <option value="">All curated feeds</option>
            {feeds.map(f => (
              <option key={f.id} value={String(f.id)}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-400 text-sm">No new articles to curate</p>
          <p className="text-gray-400 text-xs mt-1">
            Feed poll runs every 20 minutes. Only articles from the last 7 days are shown.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => {
            const action = actionMap[article.id];
            const busy = action !== undefined;

            return (
              <div key={article.id} className="card p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Meta row */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="badge-gray text-xs">{article.feed_name}</span>
                      <span className="badge-gray text-xs">{article.source_type}</span>
                      {article.original_author && (
                        <span className="text-gray-400 text-xs">{article.original_author}</span>
                      )}
                      <span className="text-gray-400 text-xs">{relativeTime(article.original_published_at ?? article.created_at)}</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-medium text-gray-900 leading-snug">
                      {article.original_title}
                    </h3>

                    {/* Excerpt */}
                    {article.original_body && (
                      <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                        {truncate(article.original_body, 300)}
                      </p>
                    )}

                    {/* URL */}
                    <a
                      href={article.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-500 hover:underline mt-1 block truncate"
                    >
                      {article.original_url}
                    </a>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleAdhoc(article.id)}
                      disabled={busy}
                      className="btn btn-primary btn-sm disabled:opacity-50"
                      title="Import for immediate individual rewrite (standard tier)"
                    >
                      {action === "adhoc" ? "Importing…" : "Ad-hoc Rewrite"}
                    </button>
                    <button
                      onClick={() => handleBatch(article.id)}
                      disabled={busy}
                      className="btn btn-ghost btn-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                      title="Queue for batch rewrite at scheduled time (cheaper)"
                    >
                      {action === "batch" ? "Queuing…" : "Queue for Batch"}
                    </button>
                    <button
                      onClick={() => handleDismiss(article.id)}
                      disabled={busy}
                      className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {action === "dismissing" ? "Dismissing…" : "Dismiss"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}